/**
 * Pure list algebra for `layout.overlays[]` — the on-screen CTA the Studio
 * inspector edits (spec §4.3, "Overlays").
 *
 * The patch contract for overlays is REPLACE, never merge: every edit writes
 * the WHOLE list (`{ layout: { overlays } }`), and an explicit `[]` clears
 * them (see collapsePatches / buildPromotionPlan). So the inspector needs
 * exactly three immutable operations — add / update one entry / remove one —
 * plus the two `start` conversions the "When" control needs. They live here,
 * DOM-free, so the fiddly parts are unit-tested in node instead of in a
 * browser QA pass.
 *
 * Nothing here clamps: the inspector's NumField already bounds every numeric
 * input, and `parseOverlays` (lib/channels.ts) is the authoritative bound
 * layer on the way into a channel. A second, divergent clamp here would be a
 * third opinion nobody reads.
 */
import type { RenderTextOverlay } from "../../../src/remotion/types.ts";

/** `start` as the type carries it: seconds into the content, or seconds
 *  before it ends. */
export type OverlayStart = RenderTextOverlay["start"];

/**
 * The overlay "Add overlay" creates. Deliberately a legible CTA parked in the
 * top-left, appearing 9s before the clip ends for 5s — every field is then
 * the operator's to move. Kept as a frozen constant so `addOverlay` can never
 * hand two callers the same mutable object.
 *
 * `fontSizePx` is canvas px on a 2160-wide frame, not CSS px: 34 rendered at
 * roughly 6 px tall on a phone, under a third the height of the caption body,
 * and read as a broken feature. 56 is a CTA. `OVERLAY_FONT_PX` in
 * `src/lib/brandStudy/mapToPatch.ts` carries the same number for studied CTAs.
 *
 * `start` is nine seconds before the END. On a clip SHORTER than that the
 * window resolves past the front of the timeline; `overlayWindow` clamps it to
 * the last `durationSec` of the content rather than showing the CTA first.
 */
export const DEFAULT_OVERLAY: Readonly<RenderTextOverlay> = Object.freeze({
  text: "Your call to action",
  fontRole: "caption",
  fontSizePx: 56,
  color: "#FFFFFF",
  xPct: 6,
  yPct: 4.5,
  align: "start",
  start: { fromEnd: 9 },
  durationSec: 5,
} satisfies RenderTextOverlay);

/** `list` + one new overlay (DEFAULT_OVERLAY, with `defaults` layered over
 *  it). Never mutates `list`. */
export function addOverlay(
  list: readonly RenderTextOverlay[],
  defaults: Partial<RenderTextOverlay> = {},
): RenderTextOverlay[] {
  return [...list, { ...DEFAULT_OVERLAY, ...defaults }];
}

/** `list` with entry `i` merged with `patch`. An out-of-range index is a
 *  no-op (returns a copy) — a stale index from a concurrent remove must never
 *  append a phantom overlay. */
export function updateOverlay(
  list: readonly RenderTextOverlay[],
  i: number,
  patch: Partial<RenderTextOverlay>,
): RenderTextOverlay[] {
  return list.map((o, idx) => (idx === i ? { ...o, ...patch } : o));
}

/** `list` without entry `i`. An out-of-range index is a no-op (returns a
 *  copy). An empty result is meaningful: `[]` CLEARS the overlays. */
export function removeOverlay(list: readonly RenderTextOverlay[], i: number): RenderTextOverlay[] {
  return list.filter((_, idx) => idx !== i);
}

/** True when the overlay is anchored to the END of the content. */
export function isFromEnd(start: OverlayStart): boolean {
  return typeof start === "object" && start !== null;
}

/** The seconds an overlay's `start` carries, whichever anchor it uses. */
export function startSeconds(start: OverlayStart): number {
  return typeof start === "object" && start !== null ? start.fromEnd : start;
}

/** Re-anchor a `start` to "from start" / "from end", KEEPING its seconds —
 *  flipping the anchor must not silently retime the overlay to 0. */
export function setStartMode(start: OverlayStart, mode: "start" | "end"): OverlayStart {
  const sec = startSeconds(start);
  return mode === "end" ? { fromEnd: sec } : sec;
}

/** One-line journal wording for an overlay's timing. */
export function startLabel(start: OverlayStart): string {
  const sec = startSeconds(start);
  return isFromEnd(start) ? `${sec}s before the end` : `${sec}s in`;
}

/** Journal-safe snippet of an overlay's text: one line, ellipsised at `max`
 *  visible chars, so a 140-char CTA can't push the step list off-screen. */
export function labelSnippet(text: string, max = 24): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}
